import { Box, Button, Flex, Grid, ScrollArea } from '@radix-ui/themes';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DurationMetadataField } from './DurationMetadataField';
import { LockedMetadataSection } from './LockedMetadataSection';
import { TagsMetadataField } from './TagsMetadataField';
import { TextAreaMetadataField } from './TextAreaMetadataField';
import { TextMetadataField } from './TextMetadataField';

import { useMetadataEditorContext } from '../context';
import { useSaveSelectedCollectionOverrides } from '../hooks/useSaveSelectedCollectionOverrides';
import { CollectionMetadata } from '../types';
import { getDraftedValues } from '../utils/getDraftedValues';

interface MetadataEditorFormProps {
    onClose: () => void;
}

export const MetadataEditorForm = ({ onClose }: MetadataEditorFormProps) => {
    const { t } = useTranslation('metadataEditor');

    const { sources } = useMetadataEditorContext();

    const {
        execute: saveOverrides,
        isLoading,
        isReady,
    } = useSaveSelectedCollectionOverrides();

    const {
        handleSubmit,
        formState: { dirtyFields, isDirty },
        reset,
    } = useFormContext<CollectionMetadata>();

    const onSubmit = async (formData: CollectionMetadata) => {
        const patchPayload = getDraftedValues(dirtyFields, formData);

        if (Object.keys(patchPayload).length === 0) return;

        try {
            const response = await saveOverrides(patchPayload);
            reset(response.savedData);
            onClose();
        } catch (error) {
            console.log('Error:', error);
        }
    };

    return (
        <Flex asChild direction='column' flexGrow='1' overflow='hidden'>
            <form
                onSubmit={handleSubmit(onSubmit)}
                onKeyDown={(e) => {
                    if (
                        e.key === 'Enter' &&
                        (e.target as HTMLElement).tagName !== 'TEXTAREA'
                    )
                        e.preventDefault();
                }}
            >
                <ScrollArea type='auto' style={{ flex: 1 }}>
                    <Flex direction='column' gap='5' p='3'>
                        <Grid
                            columns={{ initial: '1', sm: '2' }}
                            gapX='5'
                            gapY='4'
                        >
                            <Box>
                                <TextMetadataField name='country' />
                            </Box>
                            <Box>
                                <DurationMetadataField name='duration' />
                            </Box>
                            <Box style={{ gridColumn: '1 / -1' }}>
                                <TagsMetadataField
                                    name='genres'
                                    placeholder={t('fields.genresPlaceholder')}
                                />
                            </Box>
                            <Box style={{ gridColumn: '1 / -1' }}>
                                <TextAreaMetadataField
                                    name='synopsis.full'
                                    placeholder={t(
                                        'fields.synopsisPlaceholder',
                                    )}
                                />
                            </Box>
                            <Box style={{ gridColumn: '1 / -1' }}>
                                <TagsMetadataField
                                    name='credits.actors'
                                    placeholder={t(
                                        'fields.credits.actorsPlaceholder',
                                    )}
                                />
                            </Box>
                            <Box>
                                <TagsMetadataField
                                    name='credits.writers'
                                    placeholder={t(
                                        'fields.credits.writersPlaceholder',
                                    )}
                                />
                            </Box>
                            <Box>
                                <TagsMetadataField
                                    name='credits.music'
                                    placeholder={t(
                                        'fields.credits.musicPlaceholder',
                                    )}
                                />
                            </Box>
                        </Grid>

                        <LockedMetadataSection {...sources.proscope} />
                    </Flex>
                </ScrollArea>

                <Flex
                    p='4'
                    gap='3'
                    justify='end'
                    style={{
                        borderTop: '1px solid var(--gray-a4)',
                        backgroundColor: 'var(--color-panel-solid)',
                        zIndex: 10,
                    }}
                >
                    <Button
                        type='button'
                        variant='soft'
                        color='gray'
                        onClick={() => reset()}
                        disabled={!isDirty || isLoading}
                    >
                        {t('actions.reset')}
                    </Button>
                    <Button
                        type='submit'
                        variant='solid'
                        color='blue'
                        disabled={!isDirty || isLoading || !isReady}
                    >
                        {isLoading
                            ? t('actions.saving', 'Enregistrement...')
                            : t('actions.save')}
                    </Button>
                </Flex>
            </form>
        </Flex>
    );
};
